import React, { useEffect, useState } from 'react';
import { ConsentProvider, ConsentBanner } from 'react-hook-consent';
import '../../styles/cookieStyle.css';
import { getDefaultLocale } from '../../util/genericHelpers';

// ClientOnly component to ensure client-side rendering
export function ClientOnly({ children }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted ? children : null;
}

const translations = {
  necessaryCookies: {
    en: 'Necessary',
    de: 'Erforderlich',
    el: 'Απαραίτητα',
  },
  preferencesCookies: {
    en: 'Preferences',
    de: 'Präferenzen',
    el: 'Προτιμήσεις',
  },
  statisticsCookies: {
    en: 'Statistics',
    de: 'Statistiken',
    el: 'Στατιστικά',
  },
  marketingCookies: {
    en: 'Marketing',
    de: 'Marketing',
    el: 'Μάρκετινγκ',
  },
  Approve: {
    en: 'Approve',
    de: 'Genehmigen',
    el: 'Αποδοχή',
  },
  Decline: {
    en: 'Decline',
    de: 'Ablehnen',
    el: 'Απόρριψη',
  },
  Settings: {
    en: 'Settings',
    de: 'Einstellungen',
    el: 'Ρυθμίσεις',
  },
  CookieMessage: {
    en:
      'We use cookies and similar third-party technologies to personalize content and advertising, optimize your experience, and continually improve ourselves. By clicking ‘Approve,’ you agree. For more information, please refer to our privacy policy.',
    de:
      'Wir verwenden Cookies und ähnliche Technologien von Drittanbietern, um Inhalte und Werbung zu personalisieren, Ihr Erlebnis zu optimieren und uns ständig zu verbessern. Durch Klicken auf "Zustimmen" erklären Sie sich damit einverstanden. Weitere Informationen finden Sie in unserer Datenschutzrichtlinie.',
    el:
      'Χρησιμοποιούμε cookies και παρόμοιες τεχνολογίες τρίτων για να εξατομικεύσουμε περιεχόμενο και διαφημίσεις, να βελτιστοποιήσουμε την εμπειρία σας και να βελτιωνόμαστε συνεχώς. Κάνοντας κλικ στο «Αποδοχή», συμφωνείτε. Για περισσότερες πληροφορίες, ανατρέξτε στην πολιτική απορρήτου μας.',
  },
  ModalContent: {
    title: {
      en: 'Consent settings',
      de: 'Einstellungen zur Einwilligung',
      el: 'Ρυθμίσεις συγκατάθεσης',
    },
    description: {
      en:
        'We use cookies and similar third-party technologies to personalize content and advertising, optimize your experience, and continually improve ourselves. By clicking ‘Approve,’ you agree. For more information, please refer to our privacy policy.',
      de:
        'Wir verwenden Cookies und ähnliche Technologien von Drittanbietern, um Inhalte und Werbung zu personalisieren, Ihr Erlebnis zu optimieren und uns ständig zu verbessern. Durch Klicken auf "Zustimmen" erklären Sie sich damit einverstanden. Weitere Informationen finden Sie in unserer Datenschutzrichtlinie.',
      el:
        'Χρησιμοποιούμε cookies και παρόμοιες τεχνολογίες τρίτων για να εξατομικεύσουμε περιεχόμενο και διαφημίσεις, να βελτιστοποιήσουμε την εμπειρία σας και να βελτιωνόμαστε συνεχώς. Κάνοντας κλικ στο «Αποδοχή», συμφωνείτε. Για περισσότερες πληροφορίες, ανατρέξτε στην πολιτική απορρήτου μας.',
    },
    decline: {
      label: {
        en: 'Decline',
        de: 'Ablehnen',
        el: 'Απόρριψη',
      },
    },
    approve: {
      label: {
        en: 'Approve',
        de: 'Genehmigen',
        el: 'Αποδοχή',
      },
    },
    approveAll: {
      label: {
        en: 'Approve all',
        de: 'Alles genehmigen',
        el: 'Αποδοχή όλων',
      },
    },
  },
};

// Translation helper
const t = (obj, locale) => obj?.[locale] ?? obj?.en ?? '';

const consentOptionsFor = locale => ({
  services: [
    {
      id: 'necessary',
      name: t(translations.necessaryCookies, locale),
      description: t(translations.ModalContent.description, locale),
      mandatory: true,
      cookies: [{ pattern: 'necessary_*' }],
    },
    {
      id: 'preferences',
      name: t(translations.preferencesCookies, locale),
      description: t(translations.ModalContent.description, locale),
      cookies: [{ pattern: 'pref_*' }],
    },
    {
      id: 'statistics',
      name: t(translations.statisticsCookies, locale),
      description: t(translations.ModalContent.description, locale),
      cookies: [{ pattern: 'stats_*' }],
      scripts: [
        { id: 'analytics-script', src: 'https://example.com/analytics.js' }, // Replace with actual script
      ],
    },
    {
      id: 'marketing',
      name: t(translations.marketingCookies, locale),
      description: t(translations.ModalContent.description, locale),
      cookies: [{ pattern: 'marketing_*' }],
      scripts: [
        { id: 'marketing-script', src: 'https://example.com/marketing.js' }, // Replace with actual script
      ],
    },
  ],
  theme: 'dark',
});

// ConsentBannerHelper for TopbarContainer
export const ConsentBannerHelper = () => {
  const locale = getDefaultLocale();
  return (
    <ConsentBanner
      settings={{
        hidden: false,
        label: t(translations.Settings, locale),
        modal: {
          title: t(translations.ModalContent.title, locale),
          description: t(translations.ModalContent.description, locale),
          decline: { label: t(translations.ModalContent.decline.label, locale) },
          approve: { label: t(translations.ModalContent.approve.label, locale) },
          approveAll: { label: t(translations.ModalContent.approveAll.label, locale) },
        },
      }}
      decline={{ show: true, label: t(translations.Decline, locale) }}
      approve={{ label: t(translations.Approve, locale) }}
    >
      <>{t(translations.CookieMessage, locale)}</>
    </ConsentBanner>
  );
};

// ConsentWrapper to wrap the app
export const ConsentWrapper = ({ children }) => {
  const locale = getDefaultLocale();
  return (
    <ClientOnly>
      <ConsentProvider options={consentOptionsFor(locale)}>{children}</ConsentProvider>
    </ClientOnly>
  );
};
